
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Game } from '@/types/game';

interface NavItem {
  name: string;
  href: string;
  isExternal: boolean;
}

interface MobileMenuProps {
  navigation: NavItem[];
  onClose: () => void;
}

export const MobileMenu = ({ navigation, onClose }: MobileMenuProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: searchResults, isLoading: isSearchLoading } = useQuery({
    queryKey: ['search-games-mobile', debouncedSearchQuery],
    queryFn: async () => {
      if (!debouncedSearchQuery) {
        return [];
      }
      const { data, error } = await supabase
        .from('games')
        .select('id, title, thumbnail, category')
        .ilike('title', `%${debouncedSearchQuery}%`)
        .eq('hidden', false)
        .limit(5);

      if (error) throw error;
      return data;
    },
    enabled: debouncedSearchQuery.length > 0,
  });

  useOnClickOutside(searchContainerRef, () => setIsSearchFocused(false));

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchFocused(false);
      onClose();
    }
  };

  return (
    <div className="md:hidden border-t border-gray-200 py-4 space-y-4 animate-fade-in">
      {navigation.map((item) => (
        item.isExternal ? (
          <a
            key={item.name}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-300"
            onClick={onClose}
          >
            {item.name}
          </a>
        ) : (
          <Link
            key={item.name}
            to={item.href}
            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-300"
            onClick={onClose}
          >
            {item.name}
          </Link>
        )
      ))}
      <div className="px-4">
        <form onSubmit={handleSearchSubmit} ref={searchContainerRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search games..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              className="pl-10 w-full bg-gray-50 border-gray-200 focus:border-blue-500 rounded-xl"
            />
            {isSearchFocused && debouncedSearchQuery.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-100 z-10 overflow-hidden">
                {isSearchLoading ? (
                  <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : searchResults && searchResults.length > 0 ? (
                  <ul className="max-h-60 overflow-y-auto">
                    {searchResults.map((game: Partial<Game>) => (
                      <li key={game.id}>
                        <Link
                          to={`/game/${game.id}/${game.title?.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center p-3 hover:bg-gray-50"
                          onClick={() => {
                            setSearchQuery('');
                            setIsSearchFocused(false);
                            onClose();
                          }}
                        >
                          <img src={game.thumbnail} alt={game.title} className="w-10 h-10 object-cover rounded-md mr-3" />
                          <div>
                            <div className="font-semibold text-gray-800">{game.title}</div>
                            <div className="text-sm text-gray-500 capitalize">{game.category}</div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-4 text-center text-gray-500">No results found.</div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
